import { ApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  ClubBooking,
  ClubBookingListQueryParams,
  ClubBookingUpdateData,
  AvailableClubResource,
} from '../types/club-booking-management.types';
import type { IClubResourceData } from '@/types/club-resource.types';
import { PaginatedApiResponse } from '../types/api.types';

export const ClubBookingService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAvailableClubResources: builder.query<
      ApiResponse<AvailableClubResource[]>,
      {
        club_id?: string;
        resource_type?: number;
        search?: string;
        page?: number;
        limit?: number;
      }
    >({
      query: (params) => ({
        url: 'club-resources/available',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ApiResponse<AvailableClubResource[]>) =>
        response,
      providesTags: ['Bookings'],
    }),

    getAllClubBookings: builder.query<
      PaginatedApiResponse<ClubBooking>,
      ClubBookingListQueryParams
    >({
      query: (params) => ({
        url: 'club-resource-bookings',
        method: 'GET',
        params,
      }),
      transformResponse: (response: PaginatedApiResponse<ClubBooking>) =>
        response,
      providesTags: ['Bookings'],
    }),

    getClubBookingById: builder.query<ApiResponse<ClubBooking>, string>({
      query: (id) => ({
        url: `club-resource-bookings/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<ClubBooking>) => response,
    }),

    updateClubBookingStatus: builder.mutation<
      ApiResponse<ClubBooking>,
      {
        id: string;
        update: { status: number; cancellation_reason?: string };
      }
    >({
      query: ({ id, update }) => ({
        url: `club-resource-bookings/${id}/status`,
        method: 'PATCH',
        data: update,
      }),
      transformResponse: (response: ApiResponse<ClubBooking>) => response,
      invalidatesTags: ['Bookings'],
    }),

    updateClubBooking: builder.mutation<
      ApiResponse<ClubBooking & { conflictingBookings: boolean }>,
      {
        id: string;
        update: ClubBookingUpdateData;
      }
    >({
      query: ({ id, update }) => ({
        url: `club-resource-bookings/${id}/admin-update`,
        method: 'PATCH',
        data: update,
      }),
      transformResponse: (
        response: ApiResponse<ClubBooking & { conflictingBookings: boolean }>
      ) => response,
      invalidatesTags: ['Bookings'],
    }),

    // Cancel a confirmed club booking (handles refund logic server-side)
    cancelClubBooking: builder.mutation<
      ApiResponse<ClubBooking>,
      { id: string; data?: { cancellation_reason?: string } }
    >({
      query: ({ id, data }) => ({
        url: `club-resource-bookings/${id}/cancel`,
        method: 'POST',
        data: data || {},
      }),
      transformResponse: (response: ApiResponse<ClubBooking>) => response,
      invalidatesTags: ['Bookings'],
    }),

    // Create a new club booking (admin-only)
    createClubBooking: builder.mutation<
      ApiResponse<ClubBooking>,
      {
        user_id: string;
        resource_id: string;
        club_id: string;
        start_date: string;
        end_date: string;
        start_time?: string;
        end_time?: string;
        quantity: number;
        special_requests?: string;
        payment_type: number;
      }
    >({
      query: (data) => ({
        url: 'club-resource-bookings',
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<ClubBooking>) => response,
      invalidatesTags: ['Bookings'],
    }),

    // Return club resource keys
    returnClubResourceKeys: builder.mutation<
      ApiResponse<{ message: string }>,
      {
        items: Array<{
          item_id: string;
          quantity: number;
        }>;
      }
    >({
      query: (data) => ({
        url: 'club-resource-bookings/keys/return',
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<{ message: string }>) =>
        response,
      invalidatesTags: ['Bookings'],
    }),

    // Assign inspector to inspection
    assignInspector: builder.mutation<
      void,
      { orderId: string; itemId: string; inspector_id: string }
    >({
      query: ({ orderId, itemId, inspector_id }) => ({
        url: `club-resource-bookings/orders/${orderId}`,
        method: 'PATCH',
        data: {
          items: [
            {
              id: itemId,
              inspector_id,
            },
          ],
        },
      }),
      invalidatesTags: ['Bookings'],
    }),

    completeInspection: builder.mutation<
      void,
      { itemId: string; quantity: number }
    >({
      query: ({ itemId, quantity }) => ({
        url: `/club-resource-bookings/inspections/${itemId}/complete`,
        method: 'POST',
        data: { quantity },
      }),
      invalidatesTags: ['Bookings'],
    }),

    // Fetch inspector assignments (slots) for inspections by club
    getInspectorAssignments: builder.query<
      ApiResponse<
        Array<{
          id: string;
          date: string; // YYYY-MM-DD
          start_time: string; // HH:mm
          end_time: string; // HH:mm
          resource: { id: string; name: string };
          inspector: { id: string; name: string | null; email: string };
          booked: boolean;
        }>
      >,
      { club_id: string; member_id?: string }
    >({
      query: ({ club_id, member_id }) => {
        const params: { club_id: string; member_id?: string } = { club_id };
        if (member_id) params.member_id = member_id;
        return {
          url: `club-resource-bookings/inspections/assignments`,
          method: 'GET',
          params,
        };
      },
      transformResponse: (
        response: ApiResponse<
          Array<{
            id: string;
            date: string;
            start_time: string;
            end_time: string;
            resource: { id: string; name: string };
            inspector: { id: string; name: string | null; email: string };
            booked: boolean;
          }>
        >
      ) => response,
      providesTags: ['Bookings'],
      keepUnusedDataFor: 0,
    }),

    instantCreateInspectionBooking: builder.mutation<
      ApiResponse<ClubBooking>,
      {
        resource: Pick<
          IClubResourceData,
          | 'name'
          | 'resource_type'
          | 'club_id'
          | 'description'
          | 'translations'
          | 'status'
          | 'slot_duration_minutes'
          | 'inspector_ids'
        > & {
          language: string;
        };
        member_id: string;
        boat_id: string;
      }
    >({
      query: (data) => ({
        url: 'club-resource-bookings/inspections/instant-create-book',
        method: 'POST',
        data,
      }),
      transformResponse: (response: ApiResponse<ClubBooking>) => response,
      invalidatesTags: ['Bookings'],
    }),
  }),
});

export const {
  useGetAllClubBookingsQuery,
  useGetClubBookingByIdQuery,
  useUpdateClubBookingStatusMutation,
  useUpdateClubBookingMutation,
  useCancelClubBookingMutation,
  useGetAvailableClubResourcesQuery,
  useCreateClubBookingMutation,
  useReturnClubResourceKeysMutation,
  useAssignInspectorMutation,
  useCompleteInspectionMutation,
  useGetInspectorAssignmentsQuery,
  useLazyGetInspectorAssignmentsQuery,
  useInstantCreateInspectionBookingMutation,
} = ClubBookingService;
